import { CoreInput } from '@/components/Form/InputAntForm';
import CoreSelect from '@/components/Form/SelectAntForm';
import { useCoreContext } from '@/core';
import { Typography } from 'antd';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
  className: string;
  showId?: any;
  detailCenter: any;
}

export const AddressInput = (props: Props) => {
  const { className, showId, detailCenter } = props;
  const { control, watch, setValue } = useFormContext();
  const { countryData, cityData, districtData, wardData, actionGetAllDistrict, actionGetAllWard } =
    useCoreContext();
  const watchCountry = watch('address.countryCode');
  const watchCity = watch('address.cityId');
  const watchDistrict = watch('address.districtId');
  const watchWard = watch('address.wardId');

  const scopedDistrictData = useMemo(
    () => (districtData ?? []).filter((i: any) => get(i, 'city') === watchCity),
    [districtData, watchCity],
  );
  const scopedWardData = useMemo(
    () => (wardData ?? []).filter((i: any) => get(i, 'district') === watchDistrict),
    [wardData, watchDistrict],
  );

  const prevCity = useRef<any>(watchCity);
  const prevDistrict = useRef<any>(watchDistrict);

  useEffect(() => {
    const cityChanged = Boolean(prevCity.current) && prevCity.current !== watchCity;
    prevCity.current = watchCity;
    if (watchCity) {
      actionGetAllDistrict({ city: watchCity });
      if (showId === null || cityChanged) {
        setValue('address.districtId', '');
        setValue('address.wardId', '');
        setValue('address.address', '');
      }
    } else {
      setValue('address.districtId', '');
      setValue('address.wardId', '');
    }
  }, [watchCity]);

  useEffect(() => {
    const districtChanged = Boolean(prevDistrict.current) && prevDistrict.current !== watchDistrict;
    prevDistrict.current = watchDistrict;
    if (watchDistrict) {
      actionGetAllWard({ district: watchDistrict });
      if (showId === null || districtChanged) {
        setValue('address.wardId', '');
        setValue('address.address', '');
      }
    } else {
      setValue('address.wardId', '');
    }
  }, [watchDistrict]);

  useEffect(() => {
    if (showId === null || watchWard !== get(detailCenter, 'address.ward.id')) {
      setValue('address.address', '');
    }
    if (!watchWard) {
      setValue('address.wardId', '');
    }
  }, [watchWard]);

  useEffect(() => {
    if (showId === null || watchCountry !== get(detailCenter, 'address.countryCode')) {
      setValue('address.wardId', '');
      setValue('address.districtId', '');
      setValue('address.cityId', '');
      setValue('address.address', '');
    }
    if (!watchCountry) {
      setValue('address.countryCode', '');
    }
  }, [watchCountry]);

  const renderContent = useCallback(() => {
    if (watchCountry === 'VN') {
      return (
        <>
          <CoreSelect
            control={control}
            name='address.cityId'
            optionsProps={cityData ?? []}
            displayField='name'
            valueField='id'
            className='w-1/6'
            showSearch
            filterOption={(inputValue, option) => {
              if (option) {
                return String(get(option, 'label'))
                  .toLowerCase()
                  .includes(inputValue.toLowerCase());
              }
              return true;
            }}
          />
          <CoreSelect
            control={control}
            name='address.districtId'
            optionsProps={scopedDistrictData}
            displayField='name'
            valueField='id'
            className='w-1/6'
            defaultValue={''}
            showSearch
            filterOption={(inputValue, option) => {
              if (option) {
                return String(get(option, 'label'))
                  .toLowerCase()
                  .includes(inputValue.toLowerCase());
              }
              return true;
            }}
          />
          <CoreSelect
            control={control}
            name='address.wardId'
            optionsProps={scopedWardData}
            displayField='name'
            valueField='id'
            className='w-1/6'
            showSearch
            filterOption={(inputValue, option) => {
              if (option) {
                return String(get(option, 'label'))
                  .toLowerCase()
                  .includes(inputValue.toLowerCase());
              }
              return true;
            }}
          />
        </>
      );
    }
    return (
      <>
        <CoreInput control={control} name='address.city' className='w-1/6' />
        <CoreInput control={control} name='address.district' className='w-1/6' />
        <CoreInput control={control} name='address.ward' className='w-1/6' />
      </>
    );
  }, [watchCountry, cityData, scopedDistrictData, scopedWardData]);

  return (
    <div className={className}>
      <Typography>Country / City / District / Address</Typography>
      <div className='w-full flex flex-nowrap gap-[8px] mt-3'>
        <CoreSelect
          control={control}
          name='address.countryCode'
          optionsProps={get(countryData, 'data', [])}
          displayField='name'
          valueField='code'
          className='w-1/6'
          showSearch
          filterOption={(inputValue, option) => {
            if (option) {
              return String(get(option, 'label')).toLowerCase().includes(inputValue.toLowerCase());
            }
            return true;
          }}
        />
        {renderContent()}
        <CoreInput control={control} name='address.address' className='w-1/3' />
      </div>
    </div>
  );
};
